// Formatting helpers. The core honesty rule lives here: an unknown value is
// null and renders as an em dash — never 0, never a guess.

export const EMDASH = '—';

function isNum(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

export function fmtInt(n: number | null | undefined): string {
  return isNum(n) ? n.toLocaleString('en-US') : EMDASH;
}

/** Input is a 0..100 share. */
export function fmtPercent(n: number | null | undefined, digits = 1): string {
  return isNum(n) ? `${n.toFixed(digits)}%` : EMDASH;
}

/** Input is a 0..1 probability. */
export function fmtProbability(p: number | null | undefined, digits = 0): string {
  return isNum(p) ? `${(p * 100).toFixed(digits)}%` : EMDASH;
}

/** Margin in percentage points, always signed-positive with a "pt" suffix. */
export function fmtMarginPts(n: number | null | undefined, digits = 1): string {
  return isNum(n) ? `${n.toFixed(digits)} pts` : EMDASH;
}

export function fmtCurrency(
  n: number | null | undefined,
  opts: { compact?: boolean } = {},
): string {
  if (!isNum(n)) return EMDASH;
  const compact = opts.compact ?? true;
  if (compact) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: n >= 1_000_000 ? 1 : 0,
    }).format(n);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

/** Share of `part` in `whole`, as 0..100. null when whole is missing/zero. */
export function pctOf(part: number | null, whole: number | null): number | null {
  if (!isNum(part) || !isNum(whole) || whole <= 0) return null;
  return (part / whole) * 100;
}

/** Ratio 0..1 of a value within [0, max], clamped. For bar widths. */
export function ratio(value: number | null, max: number | null): number {
  if (!isNum(value) || !isNum(max) || max <= 0) return 0;
  return Math.max(0, Math.min(1, value / max));
}
